import { generateId } from 'utils/generateId';
import { TableColumn, TableRow, TableCell } from '../Table/Table.utils';
import { QuoteLineItem } from 'features/salesforce/salesforceTypes';

interface PricingResume {
  subtotal: number;
  discount: number;
  totalPrice: number;
  tax: number;
  shippingHandling: number;
  grandTotal: number;
}

export interface PricingTableContent {
  activeProduct: QuoteLineItem | null;
  columns: TableColumn[];
  rows: TableRow[];
  cells: Record<string, TableCell>;
  headerColor: string;
  hideDiscountColumn: boolean;
  hideListPriceColumn: boolean;
  hideSummary: boolean;
  hideDescriptionColumn?: boolean;
  showDescriptionAsImage?: boolean;
  quoteName?: string;
  products?: QuoteLineItem[];
  resume?: PricingResume;
}

export const hideColumnByName = (columnName: string, table: PricingTableContent) => {
  const { columns, cells } = table;
  const listPriceColumn = columns.find(column => column.header === columnName);

  if (!listPriceColumn) {
    return;
  }

  const listPriceCellIds = listPriceColumn.cellIds;

  const updatedColumns = columns.map(column => {
    if (column.id === listPriceColumn.id) {
      return { ...column, hidden: true };
    }
    return column;
  });

  const updatedCells = { ...cells };
  listPriceCellIds.forEach(cellId => {
    if (updatedCells[cellId]) {
      updatedCells[cellId] = { ...updatedCells[cellId], hidden: true };
    }
  });

  return {
    ...table,
    columns: updatedColumns,
    cells: updatedCells,
  };
};

export const showColumnByName = (columnName: string, table: PricingTableContent) => {
  const { columns, cells } = table;
  const listPriceColumn = columns.find(column => column.header === columnName);

  if (!listPriceColumn) {
    return;
  }

  const listPriceCellIds = listPriceColumn.cellIds;

  const updatedCells = { ...cells };
  listPriceCellIds.forEach(cellId => {
    if (updatedCells[cellId]) {
      updatedCells[cellId] = { ...updatedCells[cellId], hidden: false };
    }
  });

  const updatedColumns = columns.map(column => {
    if (column.id === listPriceColumn.id) {
      return { ...column, hidden: false };
    }
    return column;
  });

  return {
    ...table,
    columns: updatedColumns,
    cells: updatedCells,
  };
};

export const calculateResume = (
  quoteProducts: QuoteLineItem[],
  tax: number | null,
  shippingHandling: number | null
): PricingResume => {
  let totalSubtotal = 0;
  let totalDiscount = 0;
  let totalTotalPrice = 0;
  let totalGrandTotal = 0;

  quoteProducts.forEach(product => {
    totalSubtotal += product.TotalPrice || 0;
    totalDiscount += (product.Discount / 100) * (product.TotalPrice || 0);
    totalTotalPrice += product.TotalPrice || 0;
    totalGrandTotal += (1 - product.Discount / 100) * (product.TotalPrice || 0);
  });

  return {
    subtotal: totalSubtotal,
    discount: totalDiscount,
    totalPrice: totalTotalPrice,
    tax: tax || 0,
    shippingHandling: shippingHandling || 0,
    grandTotal: totalGrandTotal,
  };
};

export const parseQuoteLineItemsToPricingTable = (
  products: QuoteLineItem[],
  tax: number | null,
  shippingHandling: number | null,
  headerColor: string,
  headers: string[],
  hideDiscountColumn: boolean,
  hideListPriceColumn: boolean,
  hideSummary: boolean,
  hideDescriptionColumn?: boolean,
  showDescriptionAsImage?: boolean
): PricingTableContent => {
  const columns: TableColumn[] = [
    { id: 'column-1', header: headers[0], cellIds: [] },
    { id: 'column-7', header: headers[1] || 'Description', cellIds: [] },
    { id: 'column-2', header: headers[2], cellIds: [] },
    { id: 'column-3', header: headers[3], cellIds: [] },
    { id: 'column-4', header: headers[4], cellIds: [] },
    { id: 'column-5', header: headers[5], cellIds: [] },
    { id: 'column-6', header: headers[6], cellIds: [] },
  ];

  const rows: TableRow[] = [];
  const cells: Record<string, TableCell> = {};

  products.forEach((product, index) => {
    const rowId = `row-${index + 1}`;
    const cellIds: string[] = [];

    columns.forEach((column, columnIndex) => {
      const cellId = generateId();
      cellIds.push(cellId);

      let content = '';
      switch (column.id) {
        case 'column-1':
          content = product.Name;
          break;
        case 'column-2':
          content = product.ListPrice.toString();
          break;
        case 'column-3':
          content = product.UnitPrice.toString();
          break;
        case 'column-4':
          content = product.Quantity.toString();
          break;
        case 'column-5':
          content = product.Discount ? product.Discount.toString() : '0';
          break;
        case 'column-6':
          content = product.TotalPrice.toString();
          break;
        case 'column-7':
          content = product.Description;
          break;
        default:
          break;
      }

      cells[cellId] = {
        id: cellId,
        content,
      };

      columns[columnIndex].cellIds.push(cellId);
    });

    rows.push({ id: rowId, cellIds });
  });

  const resume = calculateResume(products, tax, shippingHandling);

  const hideDescriptionOnUndefined = typeof hideDescriptionColumn === 'undefined' ? true : hideDescriptionColumn;

  let parsedTable: PricingTableContent = {
    activeProduct: null,
    columns,
    rows,
    cells,
    products,
    headerColor,
    hideDiscountColumn,
    hideListPriceColumn,
    hideSummary,
    hideDescriptionColumn: hideDescriptionOnUndefined,
    showDescriptionAsImage,
    resume,
  };

  if (hideDescriptionOnUndefined) {
    const parsedTableHidedImageColumn = hideColumnByName(headers[1] || 'Description', parsedTable);
    if (parsedTableHidedImageColumn) {
      parsedTable = parsedTableHidedImageColumn;
    }
  }

  if (hideDiscountColumn) {
    const parsedTableHidedDiscountColumn = hideColumnByName(headers[5], parsedTable);
    if (parsedTableHidedDiscountColumn) {
      parsedTable = parsedTableHidedDiscountColumn;
    }
  }

  if (hideListPriceColumn) {
    const parsedTableHidedListPriceColumn = hideColumnByName(headers[2], parsedTable);
    if (parsedTableHidedListPriceColumn) {
      parsedTable = parsedTableHidedListPriceColumn;
    }
  }

  return parsedTable;
};
